import React, { useEffect, useState } from 'react';
import { X, AlertCircle, Loader2, ExternalLink, RefreshCw } from 'lucide-react';

interface ImagePreviewModalProps {
  imageUrl: string;
  isOpen: boolean;
  onClose: () => void;
}

const ImagePreviewModal: React.FC<ImagePreviewModalProps> = ({
  imageUrl,
  isOpen,
  onClose
}) => {
  const [imgLoaded, setImgLoaded] = useState(false);
  const [imgError, setImgError] = useState(false);
  const [errorMessage, setErrorMessage] = useState('Failed to load image. The URL may be invalid.');
  const [imageUrlWithCache, setImageUrlWithCache] = useState('');
  const [usingFallback, setUsingFallback] = useState(false);
  const [fallbackUrl, setFallbackUrl] = useState('');

  useEffect(() => {
    if (isOpen) {
      // Reset states when modal opens
      setImgLoaded(false);
      setImgError(false);
      setUsingFallback(false);
      setErrorMessage('Failed to load image. The URL may be invalid.');
      
      // Add cache-busting parameter to prevent browser caching
      if (imageUrl) {
        const cacheBuster = `?t=${Date.now()}`;
        setImageUrlWithCache(imageUrl.includes('?') ? `${imageUrl}&cb=${Date.now()}` : `${imageUrl}${cacheBuster}`);
        
        // Validate image URL
        try {
          // Simple URL validation
          new URL(imageUrl);
        } catch (e) {
          setImgError(true);
          setErrorMessage('Invalid image URL format.');
        }
      } else {
        setImgError(true);
        setErrorMessage('No image URL provided.');
      }
    }
  }, [isOpen, imageUrl]);

  const handleImageError = () => {
    setImgError(true);
    console.error(`Failed to load image from URL: ${imageUrl}`);
    
    // Try to determine if this is a Supabase storage URL
    const isSupabaseUrl = imageUrl.includes('supabase') || 
                          imageUrl.includes('company-assets') || 
                          imageUrl.includes('business-files');
    
    // If it's a Supabase URL, try to extract the file name and create a fallback URL
    if (isSupabaseUrl && !usingFallback) {
      try {
        // Extract filename from URL
        const urlObj = new URL(imageUrl);
        const pathParts = urlObj.pathname.split('/');
        const filename = pathParts[pathParts.length - 1];
        
        // Create fallback URL from public folder
        const placeholderUrl = `/placeholder.svg`;
        
        console.log('Trying fallback image:', placeholderUrl);
        setFallbackUrl(placeholderUrl);
        setUsingFallback(true);
        setImgError(false);
        setErrorMessage('Using placeholder image due to storage access issues.');
        
        // Provide more specific error messages
        const bucketIndex = pathParts.findIndex(part => 
          ['company-assets', 'business-files', 'documents', 'companies'].includes(part)
        );
        
        if (bucketIndex === -1) {
          setErrorMessage('The URL does not appear to reference a valid storage bucket. Using placeholder image.');
        } else {
          const bucket = pathParts[bucketIndex];
          setErrorMessage(`Failed to load image from the "${bucket}" bucket. Using placeholder image instead. Storage policies might need review.`);
        }
      } catch (e) {
        // Keep default error message and don't attempt fallback
        setErrorMessage('Failed to load image and unable to use fallback image.');
      }
    } else if (usingFallback) {
      // Even the fallback failed
      setErrorMessage('Both original image and fallback image failed to load. Try using an external image hosting service.');
    }
  };

  // Function to open image in new tab
  const openInNewTab = () => {
    window.open(usingFallback ? fallbackUrl : imageUrl, '_blank');
  };
  
  // Function to retry loading with original URL
  const retryOriginalImage = () => {
    setImgLoaded(false);
    setImgError(false);
    setUsingFallback(false);
    const cacheBuster = `?t=${Date.now()}`;
    setImageUrlWithCache(imageUrl.includes('?') ? `${imageUrl}&cb=${Date.now()}` : `${imageUrl}${cacheBuster}`);
  };

  // Function to manually enter an alternative URL
  const promptForAlternativeUrl = () => {
    const url = prompt('Enter an alternative image URL:', '');
    if (url) {
      setImgLoaded(false);
      setImgError(false);
      setImageUrlWithCache(url);
      setUsingFallback(true);
      setFallbackUrl(url);
    }
  };

  if (!isOpen) return null;

  return (
    <div 
      className="fixed inset-0 bg-black bg-opacity-80 flex items-center justify-center z-50 p-4" 
      onClick={onClose}
    >
      <div 
        className="relative max-w-4xl max-h-[90vh] overflow-auto bg-gray-800 rounded-lg"
        onClick={(e) => e.stopPropagation()}
      >
        {!imgLoaded && !imgError && (
          <div className="flex flex-col items-center justify-center h-[60vh] w-[80vw] max-w-4xl">
            <Loader2 className="h-10 w-10 text-white animate-spin mb-4" />
            <div className="text-white">Loading image...</div>
          </div>
        )}
        
        {imgError && (
          <div className="flex flex-col items-center justify-center h-[60vh] w-[80vw] max-w-4xl p-6 text-center">
            <AlertCircle className="h-12 w-12 text-red-400 mb-4" />
            <div className="text-red-400 font-medium text-lg mb-2">Image Failed to Load</div>
            <div className="text-gray-300">{errorMessage}</div>
            <div className="mt-4 text-gray-400 text-sm break-all">
              <span className="font-medium">URL:</span> {usingFallback ? fallbackUrl : imageUrl}
            </div>
            <div className="mt-6 flex gap-2 flex-wrap justify-center">
              <button 
                className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors flex items-center"
                onClick={() => window.location.reload()}
              >
                <RefreshCw className="h-4 w-4 mr-2" />
                Refresh Page
              </button>
              
              {imgError && (
                <button 
                  className="px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700 transition-colors flex items-center"
                  onClick={retryOriginalImage}
                >
                  <RefreshCw className="h-4 w-4 mr-2" />
                  Retry Original
                </button>
              )}
              
              <button 
                className="px-4 py-2 bg-purple-600 text-white rounded hover:bg-purple-700 transition-colors flex items-center"
                onClick={promptForAlternativeUrl}
              >
                <ExternalLink className="h-4 w-4 mr-2" />
                Use External URL
              </button>
            </div>
          </div>
        )}
        
        <img 
          src={usingFallback ? fallbackUrl : imageUrlWithCache} 
          alt="Image preview full size" 
          className={`max-w-full max-h-[85vh] object-contain ${imgLoaded ? 'block' : 'hidden'}`}
          onLoad={() => setImgLoaded(true)}
          onError={handleImageError}
        />
        
        <div className="absolute top-2 right-2 flex gap-2">
          {imgLoaded && (
            <button 
              className="p-2 bg-black bg-opacity-50 rounded-full text-white hover:bg-opacity-70 transition-all"
              onClick={(e) => {
                e.stopPropagation();
                openInNewTab();
              }}
              title="Open in new tab"
            >
              <ExternalLink className="h-5 w-5" />
            </button>
          )}
          <button 
            className="p-2 bg-black bg-opacity-50 rounded-full text-white hover:bg-opacity-70 transition-all"
            onClick={(e) => {
              e.stopPropagation();
              onClose();
            }}
            title="Close"
          >
            <X className="h-5 w-5" />
          </button>
        </div>
      </div>
    </div>
  );
};

export default ImagePreviewModal;
